import { HttpClient } from "@angular/common/http";
import { map, shareReplay, switchMap, tap } from "rxjs/operators";
import { AppStateService } from "../../core/services/app-state.service";
import { Injectable, Injector } from "@angular/core";
import angular from "angular";
import { PagedArray, PagedEntity } from "app/shared/model/app.model";
import { HasId } from "app/shared/model/properties.model";
import { BaseProjections, GeneralParameters, TemplateProjections } from "app/shared/model/request-parameter.model";
import { BatchRequestService } from "app/shared/services/batch-request.service";
import { ChoiceListService } from "ng/src/app/choice-lists/shared/choice-list.service";
import { Utils } from "ng/src/app/core/utils";
import { ChoiceListItemFetch } from "ng/src/app/notes/shared/note-entry.model";
import { HALResponse, JSON, Nullable, Nullish } from 'ng/src/app/shared/model/app.model';
import { NoteTemplateValue } from "ng/src/app/templates/shared/notetemplatevalue.model";
import { SectionalContainer } from "ng/src/app/templates/shared/template-container/sectional-container.model";
import { SubTemplateContainer } from "ng/src/app/templates/shared/template-container/subtemplate-container.model";
import { Template, TemplateCopyOverride, TemplateProjectionType, TemplateSegment, TemplateSegmentTypeId, TemplateSegmentTypeLink, TemplateType, TemplateTypeId, TemplateWithUsers, TypedTemplateSegment } from "ng/src/app/templates/shared/template.model";
import { forkJoin, Observable, of, concat } from "rxjs";
import { fromPromise } from "rxjs/internal-compatibility";
import IInjectorService = angular.auto.IInjectorService;

@Injectable({ providedIn: 'root'})
export class TemplateService {

	readonly globalTemplates$: Observable<{ [id: number]: Template }>;
	private readonly requestCache: {[req: string]: Observable<any>} = {}
	private _batchService?: BatchRequestService;

	constructor(
		private http: HttpClient,
		private choiceListService: ChoiceListService,
		private injector: Injector
	) {
		this.globalTemplates$ = this.getGlobalTemplates().pipe(
			map(templates => templates.reduce((dict, template) => {
				dict[template.id] = template;
				return dict;
			}, {} as Record<number, Template>)),
			shareReplay(1)
		);
		
		(window as any).templateService = this;
	}
	
	get batchRequestService() {
		if (this._batchService) return this._batchService;
		return this._batchService = (this.injector.get('$injector') as IInjectorService).get('batchRequest') as BatchRequestService;
	}
	
	queryTemplateSegments(query: GeneralParameters) {
		return this.http.get<HALResponse<{templateSegments: TemplateSegment[]}>>('/api/templateSegments', {
			params: query
		})
			.pipe(map(res => PagedEntity.from(res._embedded.templateSegments, res.page)));
	}
	
	fetchTemplateSegments(templateId: number): Observable<TemplateSegment[]> {
		const self = this;
		const params: GeneralParameters = {
			templateId: templateId,
			sort: ['sortOrder,asc'],
			size: 1000
		}
		
		let page = 0;
		const segments: TemplateSegment[] = [];
		
		return this.isTemplateGlobalTemplate(templateId).pipe(
			switchMap(isGlobal => {
				if (isGlobal) params['template.log.global'] = true;
				return fetchSegments(page);
			})
		).pipe(
			switchMap((initialResults) => {
				const totalPages = initialResults.page.totalPages;
				segments.push(...initialResults);
				page += 1;
				
				const fetches = new Array<Observable<TemplateSegment[]>>();
				for (; page < totalPages; page++) {
					const o = fetchSegments(page)
						.pipe(
							tap((s)=> {
								segments.push(...s);
							})
						);
					
					fetches.push(o);
				}
				
				if (fetches.length <= 0) {
					return of(segments);
				}
				
				return forkJoin([ // forkJoin to await for concat to finish
					concat(...fetches) //concat to execute in order
				]);
			}),
			map(() => segments)
		);
		
		function fetchSegments(page: number): Observable<PagedEntity<TemplateSegment>> {
			const usingParams = { ...params, page };
			return self.http.get<HALResponse<{ templateSegments: TemplateSegment[] }>>('/api/templateSegments', {
				params: usingParams
			}).pipe(
				map(r => PagedEntity.from(r._embedded.templateSegments, r.page)),
			);
		}
	}

	getAllTemplates<T extends TemplateProjections>(projection?: T) {
		const params: any  = {
			'hasUser': AppStateService.currentUser?.id?.toString() ?? '0',
			'enterNotes': 'true',
			'size': '10000'
		}
		if (projection) {
			params.projection = projection
		}
		return this.http.get<HALResponse<{ templates: TemplateProjectionType[T][] }>>('/api/templates', {
			params: params
		}).pipe(map(r => PagedEntity.from(r._embedded.templates, r.page)));
	}
	
	getTemplates<T extends TemplateProjections>(logId?: number, projection?: T): Observable<TemplateProjectionType[T][]> {
		const params: any = {
			'enterNotes': 'true',
			'size': '10000'
		}

		if (projection)
			params.projection = projection;
		
		if (AppStateService.currentUser?.id)
			params.hasUser = AppStateService.currentUser.id.toString()
		
		if (logId)
			params['logId'] = logId;

		return this.http.get<HALResponse<{ templates: TemplateProjectionType[T][] }>>('/api/templates', {params}).pipe(
			map(r => PagedEntity.from(r._embedded.templates, r.page))
		);
	}

	getTemplateById<T extends TemplateProjections>(templateId: number, projection?: T): Observable<TemplateProjectionType[T] | undefined> {
		const params: GeneralParameters = {};
		if (projection)
			params.projection = projection;

		return this.globalTemplates$.pipe(
			switchMap(globalTemplates => {
				if (templateId in globalTemplates && !projection)
					return of(globalTemplates[templateId] as TemplateProjectionType[T]);
				else if (templateId in globalTemplates) {
					params['log.global'] = true;
				}
				
				if (params['log.global'] != true) {
					params.id = templateId;
					return this.http.get<HALResponse<{ templates: TemplateProjectionType[T][] }> >('/api/templates', { params: params }).pipe(
						map(res => res._embedded.templates[0])
					);
				}
				else {
					return this.http.get<TemplateProjectionType[T]>(`/api/templates/${templateId}`, { params: params });
				}
			})
		)
	}

	getBasicNoteTemplate<T extends TemplateProjections>(logId: number, projection?: T): Observable<TemplateProjectionType[T]> {
		const params: GeneralParameters = {
			logId: logId,
			defaultTemplate: true,
			projection: projection
		}
		return this.http.get<HALResponse<{ templates: TemplateProjectionType[T][] }>>('/api/templates', { params: params })
			.pipe(map(res => res._embedded.templates[0]));
	}

	getSubTemplatesByTemplateId(templateId: number) {
		return this.http.get<HALResponse<{ templateSegments: TemplateSegment[] }>>(
			'api/templateSegments', {
				params: {
					template: templateId.toString(),
					'type.name': 'Sub Template'
				}
			}
		)
		.pipe(
			map(r => r._embedded.templateSegments)
		);
	}

	getGlobalTemplates<T extends TemplateProjections>(projection?: T): Observable<PagedEntity<TemplateProjectionType[T]>> {
		const params: GeneralParameters = {
			'log.global': true
		};
		if (projection)
			params.projection = projection;

		return this.http.get<HALResponse<{ templates: TemplateProjectionType[T][] }>>('/api/templates', { params: params })
			.pipe(
				map(res => PagedEntity.from(res._embedded.templates, res.page))
			)
	}
	
	getTemplateSegment(id: number): Observable<TemplateSegment> {
		const params: GeneralParameters = { id };
		return this.http.get<HALResponse<{ templateSegments: TemplateSegment[] }>>('/api/templateSegments', { params })
			.pipe(map(res => res._embedded.templateSegments[0]));
	}

	isTemplateGlobalTemplate(template: HasId | number): Observable<boolean> {
		const templateId = (typeof template === 'number') ? template : template.id;
		
		return this.globalTemplates$.pipe(
			map(templates => (templateId) in templates)
		)
	}
	
	isValueAbnormalOrOutOfRange(segment: TemplateSegment, ntv: Pick<NoteTemplateValue, 'stringValue' | 'numericValue'>, itemFetch?: ChoiceListItemFetch): Observable<boolean> {
		switch (segment.type.id) {
			case TemplateSegmentTypeId.NUMERIC_INPUT:
				const value = ntv.numericValue;
				if (value == null) {
					return of(false);
				}
				if (typeof segment.minValue === 'number' && typeof segment.maxValue === 'number') {
					return of(segment.maxValue < value || segment.minValue > value);
				}
				if (typeof segment.maxValue === 'number') {
					return of(segment.maxValue < value);
				}
				if (typeof segment.minValue === 'number') {
					return of(segment.minValue > value);
				}
				break;
			case TemplateSegmentTypeId.TEXT_CHOICE:
				if (ntv.stringValue == null || !segment.choiceList?.id)
					return of(false);
				
				if (itemFetch) {
					return fromPromise(itemFetch.add(segment.choiceList.id, ntv.stringValue)).pipe(
						map(item => !!item.abnormal)
					);
				}
				else {
					return this.choiceListService.fetchItems({ value: ntv.stringValue, listId: segment.choiceList.id })
						.pipe(
							map(results => results.some(item => !!item.abnormal))
						);
				}
		}
		return of(false);
	}
	
	getTemplateTypes(): Observable<HALResponse<{ templateTypes: TemplateType[] }>> {
		if (this.requestCache.templateTypes) return this.requestCache.templateTypes;
		
		return (this.requestCache.templateTypes = this.http.get<HALResponse<{ templateTypes: TemplateType[] }>>('api/templateTypes').pipe(
			shareReplay(1)
		));
	}
	
	getTemplateSegmentTypes(): Observable<HALResponse<{ templateSegmentTypes: TemplateSegmentTypeLink[] }>> {
		if (this.requestCache.segmentTypes) return this.requestCache.segmentTypes;
		
		return (this.requestCache.segmentTypes = this.http.get<HALResponse<{ templateSegmentTypes: TemplateSegmentTypeLink[] }>>('api/templateSegmentTypes').pipe(
			shareReplay(1)
		));
	}
	
	copyTemplateToLog(template: number, toLog: number, overrides: Record<number, TemplateCopyOverride>) {
		return this.http.post<Record<'id', number>>('/api/copyTemplateToLog', overrides, {
			params: {
				templateId: template,
				logId: toLog
			} as GeneralParameters
		})
	}
	
	copyTemplate(template: number) {
		return this.http.post<Record<'id', number>>('/api/copyTemplate', null, { params: { templateId: template} as GeneralParameters });
	}
	
	postTemplate(options: { name: string, templateType: string, log: string } & Record<string, any>) {
		return this.http.post<HasId>('/api/templates', options).pipe(
			switchMap(res => {
				return this.fetch({ id: res.id }, TemplateProjections.BASE)
					.pipe(
						map(res => res[0])
					);
			})
		)
	}

	fetch<P extends (TemplateProjections | BaseProjections)>(query: GeneralParameters, projection?: P) {
		query.projection = projection;

		return this.http.get<HALResponse<{ templates: TemplateProjectionType[P][] }>>('/api/templates', {
			params: query
		})
			.pipe(map(res => PagedArray.from(res._embedded.templates, res.page)));
	}
	
	updateTemplateSegment(segment: TemplateSegment, update: Record<any, any>) {
		return this.http.patch(segment._links.self.href, update).pipe(
			switchMap(res => this.queryTemplateSegments({ id: segment.id })),
			map(res => res[0])
		)
	}
	
	postTemplateSegment(segment: Record<any, any>) {
		return this.http.post('/api/templateSegments', segment)
			.pipe(map(res => (res as any).id));
	}
	
	updateTemplate<T extends TemplateProjections>(template: Template, update: Record<any, any>, projection?: T): Observable<TemplateProjectionType[T]> {
		
		return this.http.patch(`/api/templates/${template.id}`, update).pipe(
			switchMap(res => this.getTemplateById(template.id, projection) as Observable<TemplateProjectionType[T]>)
		);
	}
	
	deleteTemplate(template: Template | number) {
		const id = Utils.isValidNumber(template) ? template : template.id;
		return this.http.delete<null>(`/api/templates/${id}`);
	}
	
	deleteTemplateSegment(...segment: Array<TemplateSegment | number>) {
		const formatted = segment.map(s => typeof s === 'number' ? `/api/templateSegments/${s}` : s._links.self.href);
		return this.batchRequestService.observeAll(
			formatted.map(uri => this.batchRequestService.req('DELETE', uri))
		);
	}
	
	fetchTemplateContainer(templateId: number) {
		return forkJoin({
			template: this.fetch({ id: templateId }, TemplateProjections.BASE),
			segments: this.fetchTemplateSegments(templateId)
		})
			.pipe(
				map(result => {
					const template = result.template[0]
					return this.buildTemplateContainer(template, result.segments);
				})
			)
	}
	
	buildTemplateContainer<T extends TemplateTypeId = any>(template: Template<T>, segments: TemplateSegment[]): ContainerType<T> {
		const constructor = template.templateType.id === TemplateTypeId.INSPECTION ? SectionalContainer : SubTemplateContainer;
		return new constructor(template, segments) as ContainerType<T>;
	}
	
	moveSegments(segments: TemplateSegment[], afterSegment: TemplateSegment | 'TOP', sectionGuidance?: (s: TypedTemplateSegment<TemplateSegmentTypeId.SUB_TEMPLATE>) => Nullish<number>) {
		return this.batchRequestService.observeAll(
			segments.map((segment, idx) => {
				let body: Record<string, any> = {};
				if (idx > 0 || afterSegment !== 'TOP') {
					body.prevItemId = (idx === 0 ? afterSegment as TemplateSegment : segments[idx - 1]).id
				}
				else {
					body.sortOrder = -1;
				}
				
				if (segment.type.id === TemplateSegmentTypeId.SUB_TEMPLATE) {
					Utils.guard(sectionGuidance?.(segment as TypedTemplateSegment<TemplateSegmentTypeId.SUB_TEMPLATE>), level => {
						body.level = level;
					});
				}
				
				return this.batchRequestService.req('PATCH', segment._links.self.href, body);
			})
		);
	}
	
	copySegments(segments: TemplateSegment[], transform?: (ts: TemplateSegment, idx: number) => Nullable<Record<keyof TemplateSegment | string, string | number | boolean> | JSON.Object>) {
		const preparedDetails = segments.map((ts, idx) => {
			return {
				segmentId: ts.id,
				expressions: transform?.(ts, idx) ?? {}
			} as CopySegmentDetail
		});
		
		return this.http.post<TemplateSegment[]>('/api/copySegment', preparedDetails);
	}
	
	// A request to the server to get the difference of the cached template state compared to the server state.
	queryTemplateCache(request: TemplateQueryCacheRequest): Observable<TemplateQueryCacheResponse> {
		return this.http.post<TemplateQueryCacheResponse>('/api/templateQuery', request);
	}
}

export type TemplateQueryCacheRequest = {
	logId: number,
	// List of template ids known to the client.
	ids?: number[],
	// The time the client last updated the templates.
	updatedAfter?: Date
};

export type TemplateQueryCacheResponse = {
	// List of templates that are updated since the last update time requested.
	templates: TemplateWithUsers[],
	// List of template Ids that are removed from the server since the last update time requested
	// and are not in the ids list from the request.
	removedIds: number[]
}

type CopySegmentDetail = { segmentId: number, expressions: Record<string, string> };
type ContainerType<T extends TemplateTypeId> = T extends TemplateTypeId.INSPECTION ? SectionalContainer : SubTemplateContainer;